import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, DialogContent, FormControlLabel, Grid, Radio, RadioGroup, Stack } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import useWarehouseCreate from "src/hooks/warehouse/useWarehouseCreate";
import { enqueueSnackbar } from "notistack";
import useAppGlobal from "src/context/useAppGlobal";
import useWarehouseDetail from "src/hooks/warehouse/useWarehouseDetail";
import useWarehouseUpdate from "src/hooks/warehouse/useWarehouseUpdate";
import useAuthGlobal from "src/context/useAuthGlobal";
import FormControlInput from "src/components/shared/FormControlInput";
import FormControlSelect from "src/components/shared/FormControlSelect";
import CustomerAutocomplete from "../user/customer/CustomerAutocomplete";
import CustomerInput from "../user/customer/CustomerInput";
import { checkVietnam } from "../user/customer/utils/mapping";
import { useAddressData } from "../user/customer/utils/useAddressData";
import FieldLabel from "src/components/shared/FieldLabel";
import * as yup from "yup";
import { CHECK_RETURN_PROD_STATUS } from "src/constant";
import { useEffect } from "react";

export const mapToOptions = (data) => {
  if (Array.isArray(data?.data) && data?.data?.length > 0) {
    return data?.data?.map((v) => ({
      id: v?._id,
      value: v?._id,
      label: v?.name,
    }));
  }
};

const validationSchema = yup.object().shape({
  code: yup
    .string()
    .required("Thông tin bắt buộc")
    .max(5, "Không nhập quá 5 ký tự")
    .matches(/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]+$/, "Code phải chứa cả chữ và số"),
  name: yup.string().required("Thông tin bắt buộc"),
  type: yup.string().required("Thông tin bắt buộc"),
  managerName: yup.string().required("Thông tin bắt buộc"),
  managerPhone: yup.string().required("Thông tin bắt buộc"),
  shippingManagerName: yup.string().required("Thông tin bắt buộc"),
  shippingMangerPhone: yup.string().required("Thông tin bắt buộc"),
  detailAddress: yup.string().required("Địa chỉ cụ thể là bắt buộc"),
  province: yup
    .object()
    .required("Thành phố/ Tỉnh là bắt buộc")
    .when("country", {
      is: (country) => checkVietnam(country),
      then: (schema) => schema.required("Thành phố/ Tỉnh là bắt buộc"),
      otherwise: (schema) => schema.notRequired(),
    }),
  district: yup
    .object()
    .nullable()
    .when("addressLevel", {
      is: 3,
      then: (schema) => schema.required("Quận/ Huyện là bắt buộc"),
      otherwise: (schema) => schema.notRequired(),
    }),
  ward: yup
    .object()
    .required("Phường/ Xã là bắt buộc")
    .when("country", {
      is: (country) => checkVietnam(country),
      then: (schema) => schema.required("Phường/ Xã là bắt buộc"),
      otherwise: (schema) => schema.notRequired(),
    }),
  country: yup.object().required("Quốc gia là bắt buộc"),
  addressLevel: yup.number().required("Cấp địa chỉ là bắt buộc"),
});

export default function WarehouseCreate({ item, parent, child }) {
  const { userMe } = useAuthGlobal();
  const { closeDialog } = useAppGlobal();
  const { trigger: createWarehouse } = useWarehouseCreate();
  const { trigger: updateWarehouse } = useWarehouseUpdate(item?._id);
  const { data: warehouseDetail, isLoading } = useWarehouseDetail(item?._id);

  const warehouseDefaultValues = {
    code: warehouseDetail?.data?.code ?? "",
    name: warehouseDetail?.data?.name ?? "",
    type: warehouseDetail?.data?.type ?? parent?.type ?? CHECK_RETURN_PROD_STATUS.normal,
    managerName: warehouseDetail?.data?.managerName ?? "",
    managerPhone: warehouseDetail?.data?.managerPhone ?? "",
    shippingManagerName: warehouseDetail?.data?.shippingManagerName ?? "",
    shippingMangerPhone: warehouseDetail?.data?.shippingMangerPhone ?? "",
    detailAddress: warehouseDetail?.data?.detailAddress ?? parent?.detailAddress ?? "",
    ward: warehouseDetail?.data?.ward
      ? {
          value: warehouseDetail.data.ward._id,
          label: warehouseDetail.data.ward.name,
        }
      : parent?.ward
        ? {
            value: parent.ward._id,
            label: parent.ward.name,
          }
        : null,

    province: warehouseDetail?.data?.ward?.district?.province
      ? {
          value: warehouseDetail.data.ward.district.province._id,
          label: warehouseDetail.data.ward.district.province.name,
        }
      : warehouseDetail?.data?.ward?.province
        ? {
            value: warehouseDetail.data.ward.province._id,
            label: warehouseDetail.data.ward.province.name,
          }
        : parent?.ward?.district?.province
          ? {
              value: parent.ward.district.province._id,
              label: parent.ward.district.province.name,
            }
          : parent?.ward?.province
            ? {
                value: parent.ward.province._id,
                label: parent.ward.province.name,
              }
            : null,
    district: warehouseDetail?.data?.ward?.district
      ? {
          value: warehouseDetail.data.ward.district._id,
          label: warehouseDetail.data.ward.district.name,
        }
      : parent?.ward?.district
        ? {
            value: parent.ward.district._id,
            label: parent.ward.district.name,
          }
        : null,
    addressLevel: warehouseDetail?.data?.ward?.district || parent?.ward?.district ? 3 : 2,
    country: warehouseDetail?.data?.country
      ? {
          value: warehouseDetail.data.country._id,
          label: warehouseDetail.data.country.name,
        }
      : parent?.country
        ? {
            value: parent.country._id,
            label: parent.country.name,
          }
        : null,
  };

  const form = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: warehouseDefaultValues,
  });

  const { handleSubmit, reset, watch, setValue } = form;
  const formValues = watch();
  const { country, province, district, addressLevel } = formValues;
  const { countries, provinces, districts, wards } = useAddressData(province, district, addressLevel);

  const isVietnam = checkVietnam(country);

  const handleWarehouseCreate = async (data) => {
    let res;
    if (!item) {
      res = await createWarehouse({
        body: {
          ...data,
          ward: data?.ward?.value,
          type: data?.type ?? userMe?.warehouse?.type,
          district: data?.district?.value,
          province: data?.province?.value,
          warehouse: parent?._id ?? undefined,
          country: data?.country?.value ?? data?.country?.value,
        },
      });
    } else {
      res = await updateWarehouse({
        body: {
          ...data,
          name: data?.name ?? userMe?.warehouse?.name,
          type: data?.type,
          detailAddress: data?.detailAddress,
          ward: data?.ward?._id ?? data?.ward?.value,
          country: data?.country?._id ?? data?.country?.value,
          province: data?.province?.value,
          district: data?.district?.value,
        },
      });
    }

    if (res?.success) {
      reset();
      closeDialog();
      return enqueueSnackbar("Thành công!", { variant: "success" });
    } else {
      return enqueueSnackbar(res?.error?.message, { variant: "error" });
    }
  };

  const handleCreateWarehouseChild = async (data) => {
    console.log("test", data);
    const res = await createWarehouse({
      body: {
        ...data,
        ward: data?.ward?.id ?? data?.ward?.value ?? undefined,
        type: data?.type ?? userMe?.warehouse?.type,
        district: data?.district?.id ?? data?.district?.value ?? undefined,
        province: data?.province?.id ?? data?.province?.value ?? undefined,
        warehouse: parent?._id ?? undefined,
        country: data?.country?.id ?? data?.country?.value,
        managerName: data?.managerName,
        managerPhone: data?.managerPhone,
        shippingManagerName: data?.shippingManagerName,
        shippingMangerPhone: data?.shippingMangerPhone,
      },
    });
    if (res?.success) {
      reset();
      closeDialog();
      return enqueueSnackbar("Thành công!", { variant: "success" });
    } else {
      return enqueueSnackbar(res?.error?.message, { variant: "error" });
    }
  };

  const handleChangeAddressLevel = (level) => {
    setValue("district", undefined);
    setValue("ward", undefined);
    setValue("province", undefined);
    setValue("addressLevel", level);
  };

  useEffect(() => {
    if (!isLoading && warehouseDetail?.data) {
      reset(warehouseDefaultValues);
    }
  }, [warehouseDetail?.data, isLoading]);

  useEffect(() => {
    if (item || country?.value || !Array.isArray(countries?.data)) return;
    const vietnam = countries.data.find((v) => {
      const name = String(v?.name ?? "").toLowerCase();
      return name === "vietnam" || name === "việt nam";
    });
    if (!vietnam?._id) return;
    setValue("country", { value: vietnam._id, label: vietnam.name });
  }, [item, country?.value, countries?.data, setValue]);

  return (
    <DialogContent>
      <FormProvider {...form}>
        <Stack
          gap="12px"
          pt="12px"
        >
          <FormControlInput
            name="name"
            label="Tên kho"
            required
            placeholder="Tên kho"
          />
          <FormControlInput
            disabled={warehouseDetail?.data?.code}
            name="code"
            label="Code"
            required
            placeholder="Code"
          />
          <FormControlSelect
            name="type"
            label="Loại kho"
            disabled={child}
            required
            options={[
              { value: CHECK_RETURN_PROD_STATUS.factory, label: "Xưởng" },
              { value: CHECK_RETURN_PROD_STATUS.normal, label: "Thường" },
            ]}
            placeholder="Loại kho"
          />
          <CustomerAutocomplete
            name={`country`}
            label="Quốc gia"
            options={mapToOptions(countries)}
            xs={12}
            required
            disabled={parent?.ward}
            placeholder="Quốc gia"
          />
          <CustomerInput
            name={`detailAddress`}
            label="Địa chỉ cụ thể"
            placeholder="Địa chỉ cụ thể"
            required
            disabled={parent?.fullAddress}
          />
          {isVietnam && (
            <>
              <Grid
                item
                xs={12}
              >
                <RadioGroup
                  row
                  value={addressLevel}
                  onChange={(e) => handleChangeAddressLevel(Number(e.target.value))}
                >
                  <FormControlLabel
                    disabled={parent?.ward}
                    value={2}
                    control={<Radio />}
                    label={<FieldLabel label="Địa chỉ 2 cấp" />}
                  />
                  <FormControlLabel
                    disabled={parent?.ward}
                    value={3}
                    control={<Radio />}
                    label={<FieldLabel label="Địa chỉ 3 cấp" />}
                  />
                </RadioGroup>
              </Grid>
              <Stack
                direction="row"
                gap="12px"
                sx={{ width: "100%" }}
              >
                <Box flex={1}>
                  <CustomerAutocomplete
                    disabled={parent?.ward?.province}
                    name={`province`}
                    label="Thành phố/ Tỉnh"
                    options={mapToOptions(provinces)}
                    required
                  />
                </Box>
                {addressLevel === 3 && (
                  <Box flex={1}>
                    <CustomerAutocomplete
                      name={`district`}
                      label="Quận/ Huyện"
                      options={mapToOptions(districts)}
                      disabled={!province || parent?.ward?.district}
                      required
                    />
                  </Box>
                )}
                <Box flex={1}>
                  <CustomerAutocomplete
                    name={`ward`}
                    label="Phường/ Xã"
                    options={mapToOptions(wards)}
                    required
                    disabled={(addressLevel === 2 && !province) || parent?.ward}
                  />
                </Box>
              </Stack>
            </>
          )}
          <Stack
            direction="row"
            gap="12px"
          >
            <FormControlInput
              name="managerName"
              label="Tên người phụ trách"
              required
              placeholder="Tên người phụ trách"
            />
            <FormControlInput
              name="managerPhone"
              label="SĐT người phụ trách"
              required
              placeholder="SĐT người phụ trách"
            />
          </Stack>
          <Stack
            direction="row"
            gap="12px"
          >
            <FormControlInput
              name="shippingManagerName"
              label="Người phụ trách vận chuyển"
              required
              placeholder="Người phụ trách vận chuyển"
            />
            <FormControlInput
              name="shippingMangerPhone"
              label="SĐT Người phụ trách vận chuyển"
              required
              placeholder="SĐT Người phụ trách vận chuyển"
            />
          </Stack>
        </Stack>
        <Button
          variant="contained"
          fullWidth
          sx={{ mt: "12px" }}
          onClick={handleSubmit(!parent ? handleWarehouseCreate : handleCreateWarehouseChild)}
        >
          Xác nhận
        </Button>
      </FormProvider>
    </DialogContent>
  );
}
